import { z } from 'zod';
import { WorkspaceId, ArtistId, GigId, SongId, SetlistId, SetlistItemId } from '../ids.js';
import { Timestamps, Version, IsoTimestamp } from '../primitives.js';
import {
  SetlistItemType,
  SetlistItemStatus,
  SetlistVisibilityMode,
  CooldownKind,
} from '../enums.js';

/** Musical key reference (§20.5). Accidentals stored with display spelling. */
export const KeyRef = z.object({
  tonic: z.enum(['C', 'C#/Db', 'D', 'D#/Eb', 'E', 'F', 'F#/Gb', 'G', 'G#/Ab', 'A', 'A#/Bb', 'B']),
  mode: z.string().min(1).max(24),
  display: z.string().min(1).max(24),
});
export type KeyRef = z.infer<typeof KeyRef>;

/** §27.7 Song — private master repertoire entry. */
export const Song = Timestamps.extend({
  songId: SongId,
  workspaceId: WorkspaceId,
  artistId: ArtistId,
  title: z.string().min(1).max(200),
  normalizedTitle: z.string().min(1).max(200),
  originalArtist: z.string().max(200).optional(),
  normalizedOriginalArtist: z.string().max(200).optional(),
  aliases: z.array(z.string().max(200)).default([]),
  tags: z.array(z.string().max(40)).default([]),
  genre: z.string().max(60).optional(),
  energy: z.number().int().min(1).max(5).optional(),
  tempo: z.object({ min: z.number().int(), max: z.number().int() }).optional(),
  key: KeyRef.optional(),
  durationSeconds: z.number().int().positive().optional(),
  explicit: z.boolean().default(false),
  active: z.boolean().default(true),
  requestableDefault: z.boolean().default(true),
  privateNotes: z.string().max(2000).optional(),
  statistics: z
    .object({
      requestCount: z.number().int().nonnegative().default(0),
      lastPerformedAt: IsoTimestamp.nullable().default(null),
    })
    .default({ requestCount: 0, lastPerformedAt: null }),
  version: Version,
});
export type Song = z.infer<typeof Song>;

/** §17.2 Set-list item. Performer notes, transposition, hardware notes are private. */
export const SetlistItem = z.object({
  itemId: SetlistItemId,
  type: SetlistItemType,
  displayTitle: z.string().max(200),
  songId: SongId.nullable().default(null),
  plannedDurationSeconds: z.number().int().positive().nullable().default(null),
  plannedStartAt: IsoTimestamp.nullable().default(null),
  publicVisible: z.boolean().default(true),
  requestable: z.boolean().default(false),
  performerNotes: z.string().max(2000).optional(),
  transposition: z.string().max(40).optional(),
  tempo: z.number().int().nullable().default(null),
  rhythmNote: z.string().max(200).optional(),
  status: SetlistItemStatus.default('planned'),
  sortOrder: z.number().int(),
  version: Version,
});
export type SetlistItem = z.infer<typeof SetlistItem>;

export const SetlistSection = z.object({
  sectionId: z.string().min(1),
  name: z.string().max(80),
  items: z.array(SetlistItem).default([]),
});
export type SetlistSection = z.infer<typeof SetlistSection>;

/** §27.8 Setlist. */
export const Setlist = z.object({
  setlistId: SetlistId,
  workspaceId: WorkspaceId,
  artistId: ArtistId,
  gigId: GigId,
  version: Version,
  status: z.enum(['draft', 'published']),
  visibilityMode: SetlistVisibilityMode.default('current_and_next'),
  sections: z.array(SetlistSection).default([]),
  createdAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
  publishedAt: IsoTimestamp.nullable().default(null),
});
export type Setlist = z.infer<typeof Setlist>;

export const CooldownPolicy = z.object({
  kind: CooldownKind.default('none'),
  minutes: z.number().int().positive().optional(),
});
export type CooldownPolicy = z.infer<typeof CooldownPolicy>;

/** §27.9 SongPoolEntry — audience-safe projection of a requestable song. */
export const SongPoolEntry = z.object({
  gigId: GigId,
  songId: SongId,
  publicTitle: z.string().min(1).max(200),
  publicOriginalArtist: z.string().max(200).optional(),
  publicTags: z.array(z.string().max(40)).default([]),
  requestable: z.boolean().default(true),
  cooldownPolicy: CooldownPolicy.default({ kind: 'none' }),
  sortWeight: z.number().default(0),
  visibility: z.enum(['visible', 'hidden']).default('visible'),
  version: Version,
});
export type SongPoolEntry = z.infer<typeof SongPoolEntry>;
